import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { motion } from 'framer-motion';
import { Trophy, TrendingUp, ArrowUp, ArrowDown, Minus } from 'lucide-react';

const CampaignLeaderboard = ({ filters, reducedMotion = false }) => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  const [leaderboardData, setLeaderboardData] = useState({ rows: [], sort: 'answer_rate' });
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('answer_rate');
  const [prevOrder, setPrevOrder] = useState([]);

  const sortOptions = [
    { key: 'answer_rate', label: 'Answer Rate', unit: '%', icon: '📞' },
    { key: 'fcr', label: 'FCR', unit: '%', icon: '✓' },
    { key: 'aht', label: 'AHT', unit: 'ms', icon: '⏱️' },
    { key: 'asr_accuracy', label: 'ASR Accuracy', unit: '%', icon: '🎯' }
  ];

  useEffect(() => {
    fetchLeaderboard();
  }, [filters, sortBy]);

  const fetchLeaderboard = async () => {
    try {
      setLoading(true);
      
      // Save previous order for rank animation
      setPrevOrder(leaderboardData.rows.map(r => r.campaign_id));
      
      const response = await fetch(
        `${BACKEND_URL}/api/analytics/campaign-leaderboard?sort=${sortBy}&limit=10`
      );
      const result = await response.json();
      setLeaderboardData(result);
    } catch (error) {
      console.error('Error fetching campaign leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRankChange = (campaignId, currentIndex) => {
    const prevIndex = prevOrder.indexOf(campaignId);
    if (prevIndex === -1) return 'new';
    const change = prevIndex - currentIndex;
    return change;
  };

  const getTrophyIcon = (rank) => {
    if (rank === 0) return '🥇';
    if (rank === 1) return '🥈';
    if (rank === 2) return '🥉';
    return null;
  };

  return (
    <Card className="glass-card" data-testid="campaign-leaderboard">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-400" />
              Campaign Leaderboard
            </CardTitle>
            <p className="text-xs text-slate-400 mt-1">
              Top performing campaigns • Click metric to sort
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Sort Toggles */}
        <div className="flex flex-wrap gap-2 mb-6">
          {sortOptions.map(opt => (
            <button
              key={opt.key}
              onClick={() => setSortBy(opt.key)}
              className={`px-4 py-2 text-sm rounded-lg transition-all ${
                sortBy === opt.key
                  ? 'bg-gold text-white shadow-lg'
                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
              }`}
              data-testid={`sort-${opt.key}`}
            >
              <span className="mr-2">{opt.icon}</span>
              {opt.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-96">
            <div className="animate-pulse text-slate-400">Loading leaderboard...</div>
          </div>
        ) : leaderboardData.rows.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <p>No campaign data available yet</p>
            <p className="text-xs mt-2">Create campaigns and make calls to see leaderboard</p>
          </div>
        ) : (
          <div className="space-y-2">
            {leaderboardData.rows.map((campaign, index) => {
              const rankChange = getRankChange(campaign.campaign_id, index);
              const trophy = getTrophyIcon(index);
              // Sparkline series intentionally omitted: backend does not yet
              // provide a per-campaign timeseries. Render a placeholder until
              // /api/analytics/campaign-leaderboard returns a `series` field.

              return (
                <motion.div
                  key={campaign.campaign_id}
                  className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 hover:bg-slate-800/70 transition-all"
                  initial={reducedMotion ? {} : { opacity: 0, y: 20 }}
                  animate={{ 
                    opacity: 1, 
                    y: 0,
                    transition: { 
                      delay: index * 0.05,
                      duration: 0.3,
                      ease: 'easeOut'
                    }
                  }}
                  layout={!reducedMotion}
                  transition={{ duration: 0.3 }}
                  data-testid={`leaderboard-row-${index}`}
                >
                  <div className="flex items-center gap-4">
                    {/* Rank */}
                    <div className="flex-shrink-0 w-12 text-center">
                      {trophy ? (
                        <span className="text-3xl">{trophy}</span>
                      ) : (
                        <div className="text-2xl font-bold text-slate-500">#{index + 1}</div>
                      )}
                    </div>

                    {/* Campaign Name */}
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-200 truncate">
                        {campaign.campaign}
                      </div>
                      <div className="text-xs text-slate-500">
                        {campaign.total_calls} calls
                      </div>
                    </div>

                    {/* Metrics */}
                    <div className="grid grid-cols-4 gap-4 text-center">
                      <div>
                        <div className="text-sm font-bold text-gold">
                          {campaign.answer_rate}%
                        </div>
                        <div className="text-xs text-slate-500">Answer</div>
                      </div>
                      <div>
                        <div className="text-sm font-bold text-emerald-400">
                          {campaign.fcr}%
                        </div>
                        <div className="text-xs text-slate-500">FCR</div>
                      </div>
                      <div>
                        <div className="text-sm font-bold text-ink-500">
                          {campaign.aht_ms}ms
                        </div>
                        <div className="text-xs text-slate-500">AHT</div>
                      </div>
                      <div>
                        <div className="text-sm font-bold text-amber-400">
                          {campaign.asr_accuracy}%
                        </div>
                        <div className="text-xs text-slate-500">ASR</div>
                      </div>
                    </div>

                    {/* Sparkline Trend (placeholder — real series not yet provided by API) */}
                    <div className="flex-shrink-0 w-24 h-12 flex items-center">
                      <div className="w-16 h-6 skeleton" />
                    </div>

                    {/* Rank Change Indicator */}
                    {rankChange !== 'new' && rankChange !== 0 && (
                      <div className="flex-shrink-0 w-8">
                        {rankChange > 0 ? (
                          <div className="flex flex-col items-center text-emerald-400">
                            <ArrowUp className="w-4 h-4" />
                            <span className="text-xs">+{rankChange}</span>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center text-rose-400">
                            <ArrowDown className="w-4 h-4" />
                            <span className="text-xs">{rankChange}</span>
                          </div>
                        )}
                      </div>
                    )}
                    {rankChange === 'new' && (
                      <div className="flex-shrink-0 w-8 text-center">
                        <span className="text-xs text-gold font-semibold">NEW</span>
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CampaignLeaderboard;
